<template>
  <div id="app" :class="{ 'is-public': !isGameRoute, 'is-viewport': isViewportRoute }">
    <template v-if="isBareRoute">
      <router-view />
    </template>
    <template v-else>
      <a v-if="!isGameRoute" class="skip-link" href="#main-content">{{ t('a11y.skipToContent') }}</a>
      <PlayerShellHeader v-if="isPlayerShellRoute" />
      <GameHeader v-else-if="isGameRoute" />
      <Header v-else />
      <main id="main-content" class="main-content">
        <router-view />
      </main>
      <GameFooter v-if="isGameRoute" />
      <Footer v-else :compact="isAuthRoute" :strip="isViewportRoute" />
      <GameMasterToolbar />
      <!-- Signed in only: an anonymous visitor has no account to answer. -->
      <FeedbackConsole v-if="isSignedIn" />
    </template>
    <!--
      A single-page app changes the view without a page load, so assistive tech
      gets no announcement. This region speaks the new page name after each
      navigation.
    -->
    <p class="visually-hidden" role="status" aria-live="polite">{{ routeAnnouncement }}</p>
  </div>
</template>

<script>
import Header from './components/Header.vue';
import Footer from './components/Footer.vue';
import GameHeader from './components/GameHeader.vue';
import GameFooter from './components/GameFooter.vue';
import GameMasterToolbar from './components/GameMaster/GameMasterToolbar.vue';
import PlayerShellHeader from './components/Player/PlayerShellHeader.vue';
import FeedbackConsole from './components/Feedback/FeedbackConsole.vue';
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

export default {
  name: 'App',
  components: {
    Header,
    Footer,
    GameHeader,
    GameFooter,
    GameMasterToolbar,
    PlayerShellHeader,
    FeedbackConsole,
  },
  setup() {
    const route = useRoute();
    const store = useStore();
    const { t, te } = useI18n();

    const isSignedIn = computed(() => store.getters.isAuthenticated);

    const isGameRoute = computed(() => {
      return route.meta.game === true;
    });

    const isBareRoute = computed(() => {
      return route.meta.bare === true;
    });

    /*
     * Player-area pages carry `game: true` so they escape the marketing
     * typography in public.css, but they are not the game view and must not
     * inherit its chrome — GameHeader is an empty bar with no way to sign out.
     */
    const isPlayerShellRoute = computed(() => {
      return route.meta.playerShell === true;
    });

    /*
     * AuthShell's form area is `flex: 1`, so it claims the viewport minus the
     * header and the full marketing footer then stacks below it — /login was
     * 981px tall in a 772px window before this. These two get the compact
     * legal strip instead.
     */
    const isAuthRoute = computed(() => {
      return route.name === 'Login' || route.name === 'Register';
    });

    /*
     * Full-viewport surfaces (the world map) fill the window between the header
     * and a one-row footer instead of scrolling past a marketing footer.
     */
    const isViewportRoute = computed(() => {
      return route.meta.viewport === true;
    });

    const routeAnnouncement = ref('');
    watch(
      () => route.fullPath,
      () => {
        const key = route.meta.title;
        const name = key && te(key) ? t(key) : '';
        /* Cleared first so an identical string is still re-announced. */
        routeAnnouncement.value = '';
        nextTick(() => {
          routeAnnouncement.value = name ? t('a11y.navigatedTo', { page: name }) : '';
        });
      }
    );

    return {
      isGameRoute,
      isPlayerShellRoute,
      isAuthRoute,
      isViewportRoute,
      isBareRoute,
      isSignedIn,
      routeAnnouncement,
      t
    };
  },
};
</script>

<style>
/*
 * `height: 100%` here capped <html> at the viewport and turned <body> into the
 * scroll container, so window.scrollY stayed 0 while body.scrollTop moved.
 * That silently disabled router scroll restoration and any position: sticky.
 * #app already carries min-height, so the document can scroll normally.
 *
 * The horizontal cut is `clip`, not `hidden`: `hidden` makes the element a
 * scroll container, which becomes the sticky scrollport for everything inside
 * it. Since neither #app nor <body> ever scrolls, every position: sticky on
 * the site computed to sticky and then never stuck. `clip` cuts the overflow
 * without creating a scroll container.
 */
html, body {
  min-height: 100%;
  margin: 0;
  padding: 0;
  overflow-x: clip;
  box-sizing: border-box;
}

*, *:before, *:after {
  box-sizing: inherit;
}

#app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  position: relative;
  overflow-x: clip;
  max-width: 100vw;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  padding: 0;
  margin: 0 auto;
  max-width: 100%;
}

#app.is-viewport {
  height: 100dvh;
  min-height: 0;
  overflow: hidden;
}

#app.is-viewport .main-content {
  min-height: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .main-content {
    padding: 0;
  }
}
</style>
